import '.ep{width:616px;}.ssr-plugin'
import '.e2{padding:8px;}.ssr-plugin'
import '.a{display:flex;}.ssr-plugin'
import '.i{flex-direction:column;}.ssr-plugin'
import '.bl{gap:8px;}.ssr-plugin'
import '.h{width:100%;}.ssr-plugin'
import '.eq{padding-left:8px;}.ssr-plugin'
import '.er{padding-right:8px;}.ssr-plugin'
import '.b{align-items:center;}.ssr-plugin'
import { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react'
import { SelectInput } from '../SelectInput/index.js'
import { TextInput } from '../TextInput/index.js'
import { Button } from '../Button/index.js'
import { IconButton } from '../IconButton/index.js'
import { Text } from '../Text/index.js'
import { Menu } from '../Menu/index.js'
import { Sheet } from '../Sheet/index.js'
import { getPropType, Schema, SchemaTypeName } from '@based/sdk/schema'
import { capitalize, splitByUppercase } from '../../utils/text.js'
import { styled } from 'inlines'
import { Stack } from '../Stack/index.js'
import { Separator } from '../Separator/index.js'
import { colors } from '../../tokens/colors.js'
import { NumberInput } from '../NumberInput/index.js'
import { UiSchema } from '../../types/schema.js'
import { radius } from '../../utils/radius.js'

export type FilterGroupItem = { field: string; operator: string; value: string }
export type Filter = {
  operator: 'and' | 'or'
  items: FilterGroupItem[]
  nested?: { operator: 'and' | 'or'; group: Filter }
}

function countFilterItems(value: Filter): number {
  let count = value.items.length
  if (value.nested) count += countFilterItems(value.nested.group)
  return count
}

export type FilterInputProps = {
  schemaType: SchemaTypeName
  value?: Filter
  onApply: (value: Filter) => void
  schema?: UiSchema
  disabled?: boolean
}

export const FilterInput = ({
  schemaType,
  value = { operator: 'and', items: [] },
  onApply,
  schema,
  disabled,
}: FilterInputProps) => {
  const [filterValue, setFilterValue] = useState<Filter>(value)

  const isApplyDisabled = useMemo(() => {
    const validate = (group: Filter): boolean => {
      for (const it of group.items) {
        if (
          !it.field ||
          !it.operator ||
          it.value === undefined ||
          it.value === null ||
          it.value === ''
        ) {
          return true
        }
      }
      if (group.nested) return validate(group.nested.group)
      return false
    }
    if (!filterValue?.items.length) return true
    return validate(filterValue)
  }, [filterValue])

  const onGroupChange = useCallback((g: Filter) => {
    setFilterValue(g.items?.length ? g : g.nested ? g.nested.group : g)
  }, [])

  const onClearAll = useCallback(() => {
    const empty = { operator: 'and', items: [] } as Filter
    setFilterValue(empty)
    onApply?.(empty)
    Sheet.Close()
  }, [onApply])

  const onApplyClick = useCallback(() => {
    onApply?.(filterValue)
    Sheet.Close()
  }, [onApply, filterValue])

  const counter = useMemo(() => {
    const c = countFilterItems(value)
    return c || undefined
  }, [value])

  return (
    <Sheet>
      <Sheet.Trigger>
        <Button
          disabled={disabled}
          leadIcon="filter-on"
          variant="ghost"
          counter={counter}
        >
          Filter
        </Button>
      </Sheet.Trigger>
      <Sheet.Content>
        <div className="ep e2"
          style={{
            background: `linear-gradient(${colors.neutral10Background}), linear-gradient(${colors.neutralInverted100})`,
            border: `1px solid ${colors.neutral10}`,
            borderRadius: radius[20],
          }}
        >
          <Stack direction="column" gap={8}>
            <FilterGroupInput
              schemaType={schemaType}
              schema={schema}
              value={filterValue}
              onChange={onGroupChange}
            />

            <Separator />

            <Stack style={{ width: '100%', padding: 8 }}>
              <Button variant="border" color="red" onClick={onClearAll}>
                Clear all
              </Button>
              <Stack justify="end" gap={8} style={{ flex: 1 }}>
                <Button variant="border" color="neutral" onClick={Sheet.Close}>
                  Cancel
                </Button>
                <Button
                  variant="fill"
                  color="neutral"
                  keyHint="Enter"
                  disabled={isApplyDisabled}
                  onClick={onApplyClick}
                >
                  Apply
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </div>
      </Sheet.Content>
    </Sheet>
  )
}

type FilterGroupInputProps = {
  schemaType: SchemaTypeName
  value: Filter
  onChange: (value: Filter) => void
  nested?: boolean
  schema?: UiSchema
}

const FilterGroupInput = memo(
  ({ schemaType, value, onChange, nested, schema }: FilterGroupInputProps) => {
    const setOperator = useCallback(
      (op: 'and' | 'or') => onChange({ ...value, operator: op }),
      [onChange, value],
    )

    const removeAt = useCallback(
      (index: number) =>
        onChange({
          ...value,
          items: value.items.filter((_, i) => i !== index),
        }),
      [onChange, value],
    )

    const addFilter = useCallback(
      () =>
        onChange({
          ...value,
          items: [...value.items, { field: '', operator: '', value: '' }],
        }),
      [onChange, value],
    )

    const addGroup = useCallback(() => {
      onChange({
        ...value,
        nested: {
          operator: 'and',
          group: {
            operator: 'and',
            items: [{ field: '', operator: '', value: '' }],
            nested: value.nested,
          },
        },
      })
    }, [onChange, value])

    const setNestedOp = useCallback(
      (op: 'and' | 'or') =>
        onChange({
          ...value,
          nested: { ...value.nested!, operator: op },
        }),
      [onChange, value],
    )

    const setNestedGroupOp = useCallback(
      (op: 'and' | 'or') =>
        onChange({
          ...value,
          nested: {
            ...value.nested!,
            group: { ...value.nested!.group, operator: op },
          },
        }),
      [onChange, value],
    )

    const updateItem = useCallback(
      (idx: number, next: FilterGroupItem) =>
        onChange({
          ...value,
          items: value.items.map((e, i) => (i === idx ? next : e)),
        }),
      [onChange, value],
    )

    return (
      <div className="a i bl h"
      >
        {!nested && (
          <Stack
            style={{
              height: 36,
              paddingTop: 8,
              paddingLeft: 8,
              paddingBottom: 4,
              width: '100%',
            }}
          >
            <Stack gap={2} justify="start" style={{ width: '100%' }}>
              <Text singleLine variant="subtext-medium" color="neutral60">
                Only show items where
              </Text>
              <Menu>
                <Menu.Trigger>
                  <Button
                    variant="ghost"
                    size="small"
                    trailIcon="tiny-chevron-down"
                  >
                    {value.operator === 'and' ? 'all' : 'any'}
                  </Button>
                </Menu.Trigger>
                <Menu.Items>
                  <Menu.Item
                    keepOpenOnClick={false}
                    selected={value.operator === 'and'}
                    indentContent={value.operator !== 'and'}
                    onClick={() => setOperator('and')}
                  >
                    all
                  </Menu.Item>
                  <Menu.Item
                    keepOpenOnClick={false}
                    selected={value.operator === 'or'}
                    indentContent={value.operator !== 'or'}
                    onClick={() => setOperator('or')}
                  >
                    any
                  </Menu.Item>
                </Menu.Items>
              </Menu>
              <Text singleLine variant="subtext-medium" color="neutral60">
                below is true...
              </Text>
            </Stack>
          </Stack>
        )}

        {value.items.map((item, index) => (
          <div className="a bl eq er b"
            key={index}
          >
            <FilterGroupItemInput
              schema={schema}
              schemaType={schemaType}
              value={item}
              onChange={(v) => updateItem(index, v)}
            />
            <IconButton
              icon="close"
              size="small"
              onClick={() => removeAt(index)}
            />
          </div>
        ))}

        <div className="a bl eq er"
        >
          {!value.items.length && !nested ? (
            <Button variant="ghost" leadIcon="add" onClick={addFilter}>
              Add filter
            </Button>
          ) : (
            <Menu>
              <Menu.Trigger>
                <Button variant="ghost" leadIcon="add">
                  Add
                </Button>
              </Menu.Trigger>
              <Menu.Items>
                <Menu.Item leadIcon="filter-on" onClick={addFilter}>
                  Add filter
                </Menu.Item>
                <Menu.Item leadIcon="add-list" onClick={addGroup}>
                  Add filter group
                </Menu.Item>
              </Menu.Items>
            </Menu>
          )}
        </div>

        {value.nested && (
          <>
            <Stack
              style={{
                height: 36,
                paddingTop: 8,
                paddingLeft: 8,
                paddingBottom: 4,
              }}
            >
              <Stack gap={2} justify="start">
                <Menu>
                  <Menu.Trigger>
                    <Button
                      variant="ghost"
                      size="small"
                      trailIcon="tiny-chevron-down"
                    >
                      {value.nested.operator}
                    </Button>
                  </Menu.Trigger>
                  <Menu.Items>
                    <Menu.Item
                      keepOpenOnClick={false}
                      selected={value.nested.operator === 'and'}
                      indentContent={value.nested.operator !== 'and'}
                      onClick={() => setNestedOp('and')}
                    >
                      and
                    </Menu.Item>
                    <Menu.Item
                      keepOpenOnClick={false}
                      selected={value.nested.operator === 'or'}
                      indentContent={value.nested.operator !== 'or'}
                      onClick={() => setNestedOp('or')}
                    >
                      or
                    </Menu.Item>
                  </Menu.Items>
                </Menu>
                <Text variant="subtext-medium" color="neutral60">
                  where
                </Text>
                <Menu>
                  <Menu.Trigger>
                    <Button
                      variant="ghost"
                      size="small"
                      trailIcon="tiny-chevron-down"
                    >
                      {value.nested.group.operator === 'and' ? 'all' : 'any'}
                    </Button>
                  </Menu.Trigger>
                  <Menu.Items>
                    <Menu.Item
                      keepOpenOnClick={false}
                      selected={value.nested.group.operator === 'and'}
                      indentContent={value.nested.group.operator !== 'and'}
                      onClick={() => setNestedGroupOp('and')}
                    >
                      all
                    </Menu.Item>
                    <Menu.Item
                      keepOpenOnClick={false}
                      selected={value.nested.group.operator === 'or'}
                      indentContent={value.nested.group.operator !== 'or'}
                      onClick={() => setNestedGroupOp('or')}
                    >
                      any
                    </Menu.Item>
                  </Menu.Items>
                </Menu>
                <Text variant="subtext-medium" color="neutral60">
                  below is true...
                </Text>
              </Stack>
            </Stack>

            <FilterGroupInput
              nested
              schemaType={schemaType}
              schema={schema}
              value={value.nested.group}
              onChange={(_value) => {
                onChange(
                  _value.items.length
                    ? { ...value, nested: { ...value.nested!, group: _value } }
                    : { ...value, nested: _value.nested },
                )
              }}
            />
          </>
        )}
      </div>
    )
  },
)

type FilterGroupItemInputProps = {
  schema?: UiSchema
  schemaType: SchemaTypeName
  value: FilterGroupItem
  onChange: (value: FilterGroupItem) => void
}

const FilterGroupItemInput = memo(
  ({
    schema,
    schemaType,
    value: valueProp,
    onChange,
  }: FilterGroupItemInputProps) => {
    const [schemaProp, setSchemaProp] = useState<string>(valueProp.field)
    const [operator, setOperator] = useState<string>(valueProp.operator)
    const [value, setValue] = useState<string | number | boolean>(
      valueProp.value,
    )
    const [propType, setPropType] = useState<string | undefined>()
    const inputRef = useRef<HTMLInputElement>(null)

    const schemaPropOptions = useMemo(() => {
      if (!schema) return []
      const props = schema.types?.[schemaType]?.props || {}
      return Object.keys(props).map((e) => ({
        value: e,
        label: capitalize(splitByUppercase(e)),
      }))
    }, [schema, schemaType])

    useEffect(() => {
      if (!schemaProp && schemaPropOptions.length) {
        setSchemaProp(schemaPropOptions[0].value)
      }

      if (schema && schemaType && schemaProp) {
        setPropType(() =>
          getPropType(schema?.types?.[schemaType]?.props?.[schemaProp]),
        )
      }
    }, [schema, schemaType, schemaProp, schemaPropOptions])

    const operatorOptions = useMemo(() => {
      switch (propType) {
        case 'alias':
        case 'text':
        case 'string':
          return [
            { value: 'like', label: 'contain' },
            {
              value: '!has',
              label: 'does not contain',
              option: { lowerCase: true },
            },
            { value: '=', label: 'is' },
            { value: '!=', label: 'is not' },
          ]
        case 'number':
          return [
            { value: '=', label: '=' },
            { value: '!=', label: '≠' },
            { value: '<', label: '<' },
            { value: '>', label: '>' },
            { value: '<=', label: '≤' },
            { value: '>=', label: '≥' },
          ]
        case 'boolean':
          return [
            { value: '=', label: 'is' },
            { value: '!=', label: 'is not' },
          ]
        case 'timestamp':
          return [
            { value: '=', label: 'is' },
            { value: '!=', label: 'is not' },
            { value: '<', label: '<' },
            { value: '>', label: '>' },
          ]
        case 'reference':
          return [
            { value: 'like', label: 'contain' },
            {
              value: '!has',
              label: 'does not contain',
              option: { lowerCase: true },
            },
            { value: '=', label: 'is' },
            { value: '!=', label: 'is not' },
          ]
        case 'references':
          return [
            { value: 'exists', label: 'exists' },
            { value: '!exists', label: "doesn't exist" },
          ]
        default:
          return [
            { value: '=', label: 'is' },
            { value: '!=', label: 'is not' },
          ]
      }
    }, [propType])

    useEffect(() => {
      if (!value && !operator && operatorOptions.length) {
        if (propType !== 'references') {
          setOperator(operatorOptions[0].value)
        } else {
          setOperator(valueProp.operator)
        }
      }
    }, [operatorOptions, value, operator])

    useEffect(() => {
      inputRef.current?.focus()
    }, [schemaProp, operator])

    const emitChange = useCallback(
      (next: Partial<FilterGroupItem>) => {
        const payload: FilterGroupItem = {
          field: next.field ?? schemaProp,
          operator: next.operator ?? operator,
          value: next.value ?? (value as string),
        }
        onChange(payload)
      },
      [onChange, operator, schemaProp, value],
    )

    const handleSchemaPropChange = useCallback(
      (option: string) => {
        if (!option) return
        setSchemaProp(option)

        if (propType !== 'references') {
          setOperator(operatorOptions[0].value)
        }

        emitChange({ field: option })
      },
      [emitChange],
    )

    const handleOperatorChange = useCallback(
      (option: string) => {
        if (!option) return
        setOperator(option)
        emitChange({ operator: option })
      },
      [emitChange],
    )

    const handleValueChange = useCallback(
      (newValue: any) => {
        setValue(newValue)
        emitChange({ value: newValue })
      },
      [emitChange],
    )

    const ValueInput = useMemo(() => {
      switch (propType) {
        case 'number':
          return (
            <NumberInput
              ref={inputRef}
              value={Number(value) as number}
              onChange={handleValueChange}
            />
          )
        case 'boolean':
          return (
            <SelectInput
              value={Boolean(value) as boolean}
              onChange={handleValueChange}
              options={[
                { value: true, label: 'true' },
                { value: false, label: 'false' },
              ]}
            />
          )
        // case 'timestamp':
        //   return (
        //     <DateTimePicker
        //       value={Number(value) as number}
        //       onChange={(d) => handleValueChange((d as Date).getTime())}
        //     />
        //   )
        case 'references': {
          return null
        }

        case 'text':
        case 'alias':
        case 'string':
        default:
          return (
            <TextInput
              ref={inputRef}
              value={(value as string) ?? ''}
              onChange={handleValueChange}
            />
          )
      }
    }, [propType, value, handleValueChange])

    return (
      <>
        <SelectInput
          value={schemaProp}
          onChange={handleSchemaPropChange}
          options={schemaPropOptions}
        />
        <SelectInput
          value={operator}
          onChange={handleOperatorChange}
          options={operatorOptions}
        />
        {ValueInput}
      </>
    )
  },
)
